/**
 * Auditoria: a trilha do SERVIDOR.
 *
 * **Ela não é a auditoria do dinheiro.** O que aconteceu com o dinheiro está na fila de eventos
 * e na auditoria do notebook, que a tela de lá mostra. Esta guarda o que aconteceu com o
 * SERVIDOR: notebook inscrito e cortado, chave recusada, evento recusado, entrada no painel,
 * mês reaberto. A tela diz isso logo no alto, porque confundir as duas é fácil e caro.
 *
 * **Somente leitura, e não por educação.** A tabela é protegida por gatilho no banco contra
 * UPDATE e DELETE, igual à auditoria do notebook. Nem quem tem a senha do banco reescreve.
 */
import { useCallback, useEffect, useState, type JSX } from 'react'
import {
  ehSessaoTerminada, ErroDoServidor, servidor,
  type Eu, type LinhaDaAuditoria, type TelaDeAuditoria
} from '../api'
import { AbasDaAdministracao } from '../componentes/AbasDaAdministracao'
import { BarraDeCima } from '../componentes/BarraDeCima'

/** `2026-08-05T12:34:00Z` vira `05/08/2026 às 09:34`, no relógio de quem olha. */
function quando(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const data = d.toLocaleDateString('pt-BR')
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  return `${data} às ${hora}`
}

/**
 * As ações que se procura mais, no filtro.
 *
 * A lista é curta de propósito: um filtro com dezessete opções não se lê, e as que ficaram de
 * fora aparecem na lista como qualquer outra linha.
 */
const FILTROS = [
  { acao: '', titulo: 'tudo' },
  { acao: 'ENTRADA_NO_PAINEL_RECUSADA', titulo: 'entradas recusadas' },
  { acao: 'DISPOSITIVO_DESATIVADO', titulo: 'notebooks cortados' },
  { acao: 'DISPOSITIVO_INSCRITO', titulo: 'notebooks inscritos' },
  { acao: 'MES_REABERTO_PELO_PAINEL', titulo: 'meses reabertos' },
  { acao: 'EVENTO_RECUSADO', titulo: 'eventos recusados' }
] as const

export function Auditoria({ eu, aoSair }: { eu: Eu; aoSair: () => void }): JSX.Element {
  const [linhas, setLinhas] = useState<LinhaDaAuditoria[] | null>(null)
  const [temMais, setTemMais] = useState(false)
  const [acao, setAcao] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [buscandoMais, setBuscandoMais] = useState(false)

  const aoFalhar = useCallback((falha: unknown) => {
    if (ehSessaoTerminada(falha)) return aoSair()
    setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível carregar.')
  }, [aoSair])

  useEffect(() => {
    let vale = true
    setErro(null)
    setLinhas(null)

    servidor
      .auditoria(acao === '' ? {} : { acao })
      .then((recebido: TelaDeAuditoria) => {
        if (!vale) return
        setLinhas(recebido.linhas)
        setTemMais(recebido.tem_mais)
      })
      .catch((falha: unknown) => {
        if (vale) aoFalhar(falha)
      })

    return () => {
      vale = false
    }
  }, [acao, aoFalhar])

  async function maisParaTras(): Promise<void> {
    const ultima = linhas?.[linhas.length - 1]
    if (!ultima) return

    setBuscandoMais(true)
    try {
      const recebido = await servidor.auditoria({
        ...(acao === '' ? {} : { acao }),
        antes_de: ultima.quando
      })
      setLinhas([...(linhas ?? []), ...recebido.linhas])
      setTemMais(recebido.tem_mais)
    } catch (falha: unknown) {
      aoFalhar(falha)
    } finally {
      setBuscandoMais(false)
    }
  }

  return (
    <>
      <BarraDeCima eu={eu} aoSair={aoSair} voltarPara={{ endereco: '/', texto: 'Início' }} />

      <main className="folha" data-pronto={linhas !== null ? 'sim' : erro !== null ? 'erro' : 'nao'}>
        <h1>Auditoria do servidor</h1>
        {/* As outras duas abas só para quem administra: o Conselho lê a auditoria e só ela. */}
        {eu.administra_o_campo && <AbasDaAdministracao aquiEstou="/auditoria" />}

        <p className="explicacao">
          O que aconteceu com o <strong>servidor</strong>: notebook inscrito e cortado, chave
          recusada, evento recusado, entrada no painel, mês reaberto. O que aconteceu com o{' '}
          <strong>dinheiro</strong> está na tela de auditoria do notebook. Esta lista não se
          apaga nem se corrige, nem por quem tem a senha do banco.
        </p>

        <nav className="abas so-tela">
          {FILTROS.map((f) => (
            <button
              key={f.acao}
              className={f.acao === acao ? 'aba aqui' : 'aba'}
              onClick={() => setAcao(f.acao)}
            >
              {f.titulo}
            </button>
          ))}
        </nav>

        {erro !== null && <p className="recado" role="alert">{erro}</p>}
        {linhas === null && erro === null && <p className="recado aguarde">Carregando...</p>}

        {linhas !== null && linhas.length === 0 && (
          <p className="recado nada">Nada registrado com esse filtro.</p>
        )}

        {linhas !== null && linhas.length > 0 && (
          <>
            <ul className="trilha">
              {linhas.map((l) => (
                <li key={l.id}>
                  <span className="nome">{l.o_que_foi}</span>
                  <span className="detalhe">
                    {quando(l.quando)}
                    {l.quem !== null && <> · {l.quem}</>}
                    {l.notebook !== null && <> · {l.notebook}</>}
                    {l.congregacao !== null && <> · {l.congregacao}</>}
                  </span>
                  {l.motivo !== null && <span className="detalhe">Motivo: {l.motivo}</span>}
                  {l.detalhe !== null && Object.keys(l.detalhe).length > 0 && (
                    /*
                      O detalhe guarda ids e contagens, nunca nome de pessoa nem valor. Mostrar
                      como veio é melhor do que inventar uma frase para cada combinação possível.
                    */
                    <span className="detalhe miudo">{JSON.stringify(l.detalhe)}</span>
                  )}
                </li>
              ))}
            </ul>

            {temMais && (
              <p className="linha-form">
                <button onClick={() => void maisParaTras()} disabled={buscandoMais}>
                  {buscandoMais ? 'Buscando...' : 'Ver mais para trás'}
                </button>
              </p>
            )}
          </>
        )}
      </main>
    </>
  )
}
